import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { useToast } from '../ui/use-toast';
import { supabase } from '@/lib/supabase';
import type { ReceiptData } from '@/pages/Success';

const API_URL = import.meta.env.VITE_WORKER_URL || '';

interface OrderInfoSectionProps {
  receipt?: ReceiptData;
}

export function OrderInfoSection({ receipt }: OrderInfoSectionProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  // Check if screen is mobile (less than lg breakpoint: 1024px) immediately
  const getInitialState = () => {
    if (typeof window === 'undefined') return undefined; // SSR safety
    return window.innerWidth < 1024 ? "order-info" : undefined;
  };

  const [defaultValue, setDefaultValue] = useState<string | undefined>(getInitialState);

  // Set default accordion state based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      // Check if screen is mobile (less than lg breakpoint: 1024px)
      const isMobile = window.innerWidth < 1024;
      setDefaultValue(isMobile ? "order-info" : undefined);
    };

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <div className="space-y-4">
      <div className="p-6">
        <Accordion type="single" collapsible className="w-full" defaultValue={defaultValue} key={defaultValue}>
          <AccordionItem value="order-info" className="border-none">
            <AccordionTrigger className="text-lg md:text-xl font-semibold border-l-4 border-sky-500 pl-4 py-0 text-slate-900 hover:no-underline justify-start gap-3 [&>svg]:ml-0">
              {t('success.orderInfo.title')}
            </AccordionTrigger>
            <AccordionContent className="pt-6">
              <div className="space-y-6">

                {receipt?.metadata?.user_email && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('success.orderInfo.signInEmail')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.metadata?.user_email}</div>
                  </div>
                )}

                {receipt?.metadata?.app_user_id && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('success.orderInfo.userId')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.metadata?.app_user_id?.toUpperCase()}</div>
                  </div>
                )}

                {receipt?.metadata?.event_code && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('common.eventCode')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.metadata?.event_code}</div>
                  </div>
                )}

                {receipt?.metadata?.referral_code && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('common.referralCode')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.metadata?.referral_code}</div>
                  </div>
                )}

                {receipt?.metadata?.promotion_code && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('common.promotionCode')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.metadata?.promotion_code}</div>
                  </div>
                )}

                {receipt?.invoiceId && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('success.orderInfo.orderId')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.invoiceId}</div>
                  </div>
                )}

                {receipt?.customer?.email && (
                  <div className="flex items-start gap-2 flex-col">
                    <div className="text-xs md:text-sm text-slate-400">{t('success.orderInfo.invoiceEmail')}</div>
                    <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.customer?.email}</div>
                  </div>
                )}

                <div className="flex items-start gap-3 flex-col">
                  <div className="flex items-center gap-2 flex-row">
                    {receipt?.url && (
                      <Button
                        variant="outline"
                        asChild
                      >
                        <a
                          href={receipt.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm md:text-sm rounded break-all text-slate-600 bg-slate-100 hover:bg-slate-50"
                        >
                          {t('success.orderInfo.viewReceipt')}
                        </a>
                      </Button>
                    )}

                    {receipt?.checkoutMode === 'login_optional' ? (
                      <Button
                        variant="outline"
                        asChild
                      >
                        <a
                          href="https://billing.stripe.com/p/login/fZu6oGdrUeec6R316ygA800"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm md:text-sm rounded break-all text-slate-600 bg-slate-100 hover:bg-slate-50"
                        >
                          {t('success.orderInfo.manageSubscription')}
                        </a>
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        asChild
                      >
                        <a
                          href="#"
                          onClick={async (e) => {
                            e.preventDefault();
                            setIsLoadingPortal(true);
                            try {
                              const token = await supabase.auth.getSession().then(res => res.data.session?.access_token);
                              if (!token) throw new Error('No auth token available');

                              const response = await fetch(`${API_URL}/api/customer-portal`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Accept-Language': i18n.language
                                }
                              });

                              if (!response.ok) throw new Error('Failed to create portal session');

                              const { url } = await response.json();

                              // On mobile, navigate in same tab; on desktop, open new tab
                              if (isMobile) {
                                window.location.href = url;
                              } else {
                                window.open(url, '_blank');
                              }
                            } catch (error) {
                              console.error('Failed to create portal session:', error);
                              // Show error toast
                              toast({
                                variant: "destructive",
                                title: t('common.error'),
                                description: t('success.orderInfo.portalError'),
                              });
                              // Fallback to no-code portal URL
                              if (isMobile) {
                                window.location.href = "https://billing.stripe.com/p/login/fZu6oGdrUeec6R316ygA800";
                              } else {
                                window.open("https://billing.stripe.com/p/login/fZu6oGdrUeec6R316ygA800", '_blank');
                              }
                            } finally {
                              setIsLoadingPortal(false);
                            }
                          }}
                          className={`text-sm md:text-sm rounded break-all text-slate-600 bg-slate-100 hover:bg-slate-50 ${isLoadingPortal ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isLoadingPortal ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                              {t('common.loading')}
                            </div>
                          ) : (
                            t('success.orderInfo.manageSubscription')
                          )}
                        </a>
                      </Button>
                    )}

                  </div>

                  <p className="text-sm md:text-sm text-slate-400">
                    {t('success.orderInfo.manageSubscriptionDescription')}
                  </p>

                  {import.meta.env.DEV && (

                    <div className="flex items-start gap-2 flex-col mt-4">
                      <div className="text-xs md:text-sm text-slate-400">Customer ID</div>
                      <div className="text-sm md:text-base rounded break-all text-slate-600">{receipt?.customer?.id}</div>
                    </div>
                  )}

                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}
